import classNames from 'classnames';
import { type ComponentPropsWithoutRef, memo } from 'react';
import isEqual from 'react-fast-compare';

import { CheckIcon } from '@air/design-icons';

/**
 * Determines the text color based on the background color.
 */
const getTextColor = (hex: string) => {
  const r = parseInt(hex.substring(1, 2), 16);
  // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const g = parseInt(hex.substring(3, 2), 16);
  // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const b = parseInt(hex.substring(5, 2), 16);
  // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
  return yiq >= 128 ? 'text-black' : 'text-white';
};

export type Color = {
  hex: string;
  id: string;
  name: string;
};

export type ColorPickerProps = Omit<ComponentPropsWithoutRef<'div'>, 'onSelect'> & {
  colors?: Color[];
  onSelect?: (color: Color) => void;
  selectedColors?: Color[];
};

export const ColorPicker = memo(({ className, colors, onSelect, selectedColors, ...restOfProps }: ColorPickerProps) => {
  return (
    <div className={classNames('flex flex-wrap gap-2', className)} {...restOfProps}>
      {colors?.map((color) => {
        const isSelected = selectedColors?.some((selectedColor) => isEqual(selectedColor, color));
        const textColor = getTextColor(color.hex);

        return (
          <button
            className={classNames(
              'flex h-5 w-5 items-center justify-center rounded-4',
              color.hex === '#FFFFFF' ? 'border border-gray-4' : 'border-none',
              textColor,
            )}
            data-hex={color.hex}
            data-name={color.name}
            key={color.id || color.name || color.hex}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => onSelect?.(color)}
            style={{ backgroundColor: color.hex }}
            type="button"
          >
            <CheckIcon
              className={classNames(
                'block transition-all ease-in-out',
                isSelected ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
              )}
              size="sm"
            />
          </button>
        );
      })}
    </div>
  );
});

ColorPicker.displayName = 'ColorPicker';
